import { Dayjs } from "dayjs";
import moment from "moment";
import { Permission, User } from "../context/AuthContext";
import {
  Action,
  AssemblyConstituencyType,
  Module,
  RIOType,
  ZoneType,
} from "./types";

/* eslint-disable @typescript-eslint/no-explicit-any */
export const setItem = ({ key, value }: { key: string; value: any }) => {
  localStorage.setItem(key, value);
};

export const getItem = (key: string) => {
  return localStorage.getItem(key);
};

export const removeItem = (key: string) => {
  localStorage.removeItem(key);
};

export function getInitials(name: string) {
  if (!name) return "";

  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0][0].toUpperCase();

  return (words[0][0] + words[1][0]).toUpperCase();
}

export const formatFullDateTime = (date: string | Date): string => {
  return moment(date).format("D MMMM YYYY, hh:mm A");
};

//To do
export const getMeaningfulTimeAgo = (timestamp: string): string => {
  const now = moment();
  const created = moment(timestamp);
  const diffInSeconds = now.diff(created, "seconds");

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = now.diff(created, "minutes");
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
  }

  const diffInHours = now.diff(created, "hours");
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  }

  const diffInDays = now.diff(created, "days");
  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
  }

  const diffInMonths = now.diff(created, "months");
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths > 1 ? "s" : ""} ago`;
  }

  const diffInYears = now.diff(created, "years");
  return `${diffInYears} year${diffInYears > 1 ? "s" : ""} ago`;
};

export const getFromNow = (timestamp: string): string => {
  return moment(timestamp).fromNow();
};

export const convertISTtoUTC = (date: Dayjs): string => {
  // Subtract 5:30 hours (5.5 hours) to convert IST to UTC
  const utcDate = date.subtract(5.5, "hour");
  // Format as YYYY-MM-DDTHH:mm:ss (without milliseconds or Z)
  return utcDate.format("YYYY-MM-DDTHH:mm:ss");
};

export const hasPermission = (
  permissions: Permission[],
  module: Module,
  action: Action
): boolean => {
  const userPermissions = permissions?.find?.(
    (permission) => permission?.module === module
  );
  if (userPermissions) {
    return userPermissions.actions.includes(action);
  }

  return false; // If no permissions for the specified module are found.
};

export const getAssemblyConstituencyNameById = ({
  assemblyList,
  assemblyId,
}: {
  assemblyList: AssemblyConstituencyType[];
  assemblyId: number;
}) => {
  return assemblyList.find((ac) => ac.assemblyConstituencyId === assemblyId)
    ?.assemblyConstituencyName;
};

export const getZoneNameById = ({
  zoneList,
  zoneId,
}: {
  zoneList: ZoneType[];
  zoneId: number;
}) => {
  return zoneList.find((zone) => zone.zoneId === zoneId)?.zoneName;
};

export const getRIONameById = ({
  rioList,
  rioId,
}: {
  rioList: RIOType[];
  rioId: number;
}) => {
  return rioList.find((rio) => rio.rioId === rioId)?.rioName;
};

export const canCreateRoles = (user: User) =>
  hasPermission(user?.permissions, "Roles", "Create");
